import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  fullWidth?: boolean;
  loading?: boolean;
  children?: React.ReactNode;
}

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-9 px-[14px] text-[14px]',
  md: 'h-11 px-5 text-[15px]',
  lg: 'h-[52px] px-7 text-base',
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-green-700 text-white border-transparent shadow-sm hover:bg-green-900 active:bg-green-900',
  secondary:
    'bg-transparent text-green-700 border-green-700 hover:bg-green-700 hover:text-white',
  ghost:
    'bg-transparent text-ink-700 border-transparent hover:bg-ink-100',
  danger:
    'bg-transparent text-danger border-danger hover:bg-danger hover:text-white',
};

const ICON_SIZE: Record<Size, number> = { sm: 16, md: 18, lg: 20 };

export function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  fullWidth,
  loading,
  disabled,
  className = '',
  children,
  type = 'button',
  ...rest
}: Props) {
  const iconSize = ICON_SIZE[size];
  const isDisabled = disabled || loading;

  const classes = [
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold tracking-tight',
    'border-[1.5px] select-none whitespace-nowrap',
    'transition-[background,color,border-color,box-shadow,transform] duration-base ease-out',
    'active:scale-[0.97] btn-focus',
    SIZE_CLASSES[size],
    VARIANT_CLASSES[variant],
    fullWidth ? 'w-full' : '',
    isDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} disabled={isDisabled} {...rest}>
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" strokeWidth={2.25} />
      ) : (
        Icon && <Icon size={iconSize} strokeWidth={2.25} />
      )}
      {children}
      {!loading && IconRight && <IconRight size={iconSize} strokeWidth={2.25} />}
    </button>
  );
}
